import type { ReactElement } from 'react';
import type { LeadData, PluginConfig } from '@hub/shared-types';
import type { PluginLoadState } from '@/presentation/hooks/usePluginLoadRetry';
import { PluginLoadingScreen } from '@/presentation/components/PluginLoadingScreen';
import { PluginErrorScreen } from '@/presentation/components/PluginErrorScreen';
import { PluginActiveScreen } from '@/presentation/components/PluginActiveScreen';

export interface PluginLoadStepScreenProps {
  state: PluginLoadState;
  config: PluginConfig;
  onLeadCapture: (lead: LeadData) => void;
  onComplete: () => void;
  onRenderError: (error: unknown) => void;
  onRetry: () => void;
}

/**
 * Traduz `PluginLoadState` (HUB-237, CA-04 a CA-10) para a tela
 * correspondente: carregando/tentando novamente (`PluginLoadingScreen`),
 * plugin montado (`PluginActiveScreen`, com `key={mountKey}` — força
 * recarregar o módulo do zero a cada nova tentativa, nunca só remontar o
 * componente que acabou de falhar) ou erro final (`PluginErrorScreen`).
 */
export function PluginLoadStepScreen({
  state,
  config,
  onLeadCapture,
  onComplete,
  onRenderError,
  onRetry,
}: PluginLoadStepScreenProps): ReactElement {
  if (state.kind === 'waiting') {
    return (
      <PluginLoadingScreen
        phase={{ kind: 'waiting', attempt: state.attempt, secondsRemaining: state.secondsRemaining }}
      />
    );
  }
  if (state.kind === 'ready') {
    return (
      <PluginActiveScreen
        key={state.mountKey}
        plugin={state.plugin}
        config={config}
        onLeadCapture={onLeadCapture}
        onComplete={onComplete}
        onRenderError={onRenderError}
      />
    );
  }
  if (state.kind === 'error-final') {
    return <PluginErrorScreen offline={state.offline} onRetry={onRetry} />;
  }
  const attempt = state.kind === 'loading' ? state.attempt : 1;
  return <PluginLoadingScreen phase={{ kind: 'loading', attempt }} />;
}
